import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { authClient } from "./auth-client";

interface User {
  id: string;
  name: string;
  email: string;
  image?: string | null;
  role?: string;
}

interface Session {
  user: User;
  activeOrganizationId?: string | null;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasActiveOrganization: boolean;
  bootstrapState: "idle" | "ready" | "missing-organization" | "failed";
  bootstrapMessage: string | null;
  authCheckError: string | null;
  signOut: () => Promise<void>;
  refresh: () => Promise<AuthRefreshResult>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthClientErrorLike {
  message?: string;
}

interface AuthClientResult<T> {
  data?: T | null;
  error?: AuthClientErrorLike | null;
}

interface OrganizationRecord {
  id: string;
}

interface RawSessionUser {
  id: string;
  name: string;
  email: string;
  image?: string | null;
  role?: unknown;
  userRole?: unknown;
  user_role?: unknown;
}

interface RawSessionPayload {
  user: RawSessionUser;
  session?: Record<string, unknown> | null;
}

type AuthRefreshResult =
  | {
      status: "authenticated";
      bootstrapState: "ready" | "missing-organization" | "failed";
      message: string | null;
    }
  | {
      status: "unauthenticated";
      bootstrapState: "idle";
      message: null;
    }
  | {
      status: "error";
      bootstrapState: "failed";
      message: string;
    };

function getResultErrorMessage(result: unknown): string | null {
  const maybeResult = result as AuthClientResult<unknown> | null;
  return maybeResult?.error?.message?.trim() || null;
}

function getActiveOrganizationId(session: RawSessionPayload): string | null {
  const sessionData = session.session ?? undefined;
  const activeOrganizationId = sessionData?.activeOrganizationId;
  return typeof activeOrganizationId === "string" && activeOrganizationId.length > 0
    ? activeOrganizationId
    : null;
}

export function normalizeSessionRole(user: Pick<RawSessionUser, "role" | "userRole" | "user_role">): string | undefined {
  const candidates = [user.role, user.userRole, user.user_role];
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const trimmed = candidate.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

function toSession(session: RawSessionPayload): Session {
  return {
    user: {
      id: session.user.id,
      name: session.user.name,
      email: session.user.email,
      image: session.user.image,
      role: normalizeSessionRole(session.user),
    },
    activeOrganizationId: getActiveOrganizationId(session),
  };
}

/** Maximum retries when org list is empty (provisioning race window). */
const MAX_BOOTSTRAP_RETRIES = 3;

/** Delay between retries in ms. Enough for backend provisioning to settle. */
const BOOTSTRAP_RETRY_DELAY_MS = 800;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface ResolveSessionResult {
  session: Session;
  bootstrapState: "ready" | "missing-organization" | "failed";
  bootstrapMessage: string | null;
}

/**
 * Attempt to activate an org from the user's org list.
 * Returns the resolved state, or null if the org list is empty (caller should retry).
 */
async function tryActivateOrganization(
  session: RawSessionPayload,
): Promise<ResolveSessionResult | "no-orgs"> {
  const orgsResult = await authClient.organization.list() as AuthClientResult<OrganizationRecord[]>;
  const orgListError = getResultErrorMessage(orgsResult);
  if (orgListError) {
    return {
      session: toSession(session),
      bootstrapState: "failed",
      bootstrapMessage: "You are signed in, but your workspace list could not be loaded. Try again.",
    };
  }

  const organizations = orgsResult.data ?? [];
  const firstOrganization = organizations[0];

  if (!firstOrganization) {
    return "no-orgs";
  }

  const setActiveResult = await authClient.organization.setActive({
    organizationId: firstOrganization.id,
  });

  if (getResultErrorMessage(setActiveResult)) {
    return {
      session: toSession(session),
      bootstrapState: "failed",
      bootstrapMessage: "You are signed in, but we could not activate your workspace. Try again.",
    };
  }

  const refreshed = await authClient.getSession() as AuthClientResult<RawSessionPayload>;
  if (!refreshed.data) {
    return {
      session: toSession(session),
      bootstrapState: "failed",
      bootstrapMessage: "You are signed in, but the refreshed session did not include a workspace.",
    };
  }

  const refreshedSession = toSession(refreshed.data);
  return {
    session: refreshedSession,
    bootstrapState: refreshedSession.activeOrganizationId ? "ready" : "failed",
    bootstrapMessage: refreshedSession.activeOrganizationId
      ? null
      : "You are signed in, but no active workspace was returned after activation.",
  };
}

/**
 * Resolve the user's active organization. If none is set, attempts to fetch
 * the org list and activate the first one. Retries with a short delay to
 * handle the race window where backend provisioning (autoProvisionUserWorkspace)
 * has not yet committed the org by the time the frontend calls getSession().
 */
async function resolveSession(
  session: RawSessionPayload,
): Promise<ResolveSessionResult> {
  const existingActiveOrganizationId = getActiveOrganizationId(session);
  if (existingActiveOrganizationId) {
    return {
      session: toSession(session),
      bootstrapState: "ready",
      bootstrapMessage: null,
    };
  }

  try {
    for (let attempt = 0; attempt < MAX_BOOTSTRAP_RETRIES; attempt++) {
      if (attempt > 0) {
        await wait(BOOTSTRAP_RETRY_DELAY_MS);
      }

      const result = await tryActivateOrganization(session);

      if (result === "no-orgs") {
        continue;
      }

      return result;
    }

    return {
      session: toSession(session),
      bootstrapState: "missing-organization",
      bootstrapMessage: "Your workspace is still being set up. This usually takes a few seconds.",
    };
  } catch {
    return {
      session: toSession(session),
      bootstrapState: "failed",
      bootstrapMessage: "You are signed in, but workspace bootstrap failed unexpectedly. Try again.",
    };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [bootstrapState, setBootstrapState] = useState<AuthContextValue["bootstrapState"]>("idle");
  const [bootstrapMessage, setBootstrapMessage] = useState<string | null>(null);
  const [authCheckError, setAuthCheckError] = useState<string | null>(null);

  const fetchSession = async (): Promise<AuthRefreshResult> => {
    setIsLoading(true);
    setAuthCheckError(null);

    try {
      const result = await authClient.getSession() as AuthClientResult<RawSessionPayload>;
      if (!result.data) {
        setSession(null);
        setBootstrapState("idle");
        setBootstrapMessage(null);
        return {
          status: "unauthenticated",
          bootstrapState: "idle",
          message: null,
        };
      }

      const resolved = await resolveSession(result.data);
      setSession(resolved.session);
      setBootstrapState(resolved.bootstrapState);
      setBootstrapMessage(resolved.bootstrapMessage);

      return {
        status: "authenticated",
        bootstrapState: resolved.bootstrapState,
        message: resolved.bootstrapMessage,
      };
    } catch {
      setSession(null);
      setBootstrapState("failed");
      setBootstrapMessage(null);
      setAuthCheckError("We could not verify your authenticated session. Please try again.");

      return {
        status: "error",
        bootstrapState: "failed",
        message: "We could not verify your authenticated session. Please try again.",
      };
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    void fetchSession();
  }, []);

  const signOut = async () => {
    await authClient.signOut();
    setSession(null);
    setBootstrapState("idle");
    setBootstrapMessage(null);
    setAuthCheckError(null);
  };

  const value: AuthContextValue = {
    user: session?.user ?? null,
    session,
    isLoading,
    isAuthenticated: session !== null,
    hasActiveOrganization: Boolean(session?.activeOrganizationId),
    bootstrapState,
    bootstrapMessage,
    authCheckError,
    signOut,
    refresh: fetchSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
